import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRightIcon, ArrowLeftIcon, CheckIcon, RouteIcon, SparklesIcon, PencilIcon, SplitIcon, DatabaseIcon } from 'lucide-react';
import type { DestinationId, VendorRouting } from '../../lib/types';
import { DESTINATIONS } from '../../lib/destinations';
import { FIVE9_LISTS, TELI_CAMPAIGNS } from '../../lib/mockData';
import { SearchableSelect, type Option } from '../ui/SearchableSelect';
import { cn, formatNumber } from '../../lib/utils';
interface Props {
  selected: DestinationId[];
  onToggle: (id: DestinationId) => void;
  routing: VendorRouting[];
  onRoutingChange: (routing: VendorRouting[]) => void;
  onBack: () => void;
  onContinue: () => void;
}
export function Step3Destinations({
  selected,
  onToggle,
  routing,
  onRoutingChange,
  onBack,
  onContinue
}: Props) {
  const teliOptions: Option[] = useMemo(() => TELI_CAMPAIGNS.map(c => ({
    value: c.id,
    label: c.name,
    secondary: c.id,
    description: c.description
  })), []);
  const five9Options: Option[] = useMemo(() => FIVE9_LISTS.map(l => ({
    value: l.id,
    label: l.name,
    secondary: l.id
  })), []);
  const teliSelected = selected.includes('teli');
  const five9Selected = selected.includes('five9');
  const multiVendor = routing.length > 1;
  function setTeli(vendor: string, campaignId: string) {
    onRoutingChange(routing.map(r => r.vendor === vendor ? {
      ...r,
      teliCampaignId: campaignId,
      autoSelected: false
    } : r));
  }
  function setFive9(vendor: string, listId: string) {
    onRoutingChange(routing.map(r => r.vendor === vendor ? {
      ...r,
      five9ListId: listId,
      autoSelected: false
    } : r));
  }
  const canContinue = selected.length > 0 && (!teliSelected || routing.every(r => r.teliCampaignId)) && (!five9Selected || routing.every(r => r.five9ListId));
  return <div className="space-y-6" data-id="element-145" data-mp="1woma5ja1ac7u">
      {/* Destination selection */}
      <section data-id="element-146" data-mp="2an2cky0v5gk6">
        <h3 className="mb-1 text-sm font-semibold text-gray-900" data-id="element-147" data-mp="2anly0opq2n2y">
          Select destinations
        </h3>
        <p className="mb-3 text-sm text-gray-500" data-id="element-148" data-mp="26msa3vca58a2">
          Choose one or more places to send this batch of leads.
        </p>
        <div className="mb-4 flex items-start gap-2.5 rounded-xl border border-salesforce-100 bg-salesforce-50 px-4 py-3" data-id="element-149" data-mp="2flndkznxw49o">
          <DatabaseIcon className="mt-0.5 h-4 w-4 shrink-0 text-salesforce-600" data-id="element-150" data-mp="2uajn715n9pce" />
          <p className="text-sm text-gray-700" data-id="element-151" data-mp="2panforxqnfs3">
            <span className="font-semibold text-gray-900" data-id="element-152" data-mp="2pbyurnwmw9yi">
              Every batch is always created in Salesforce.
            </span>{' '}
            The destinations below are additional places these leads are sent.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2" data-id="element-153" data-mp="3hxwz5t9n5utk">
          {DESTINATIONS.map(d => {
          const isOn = selected.includes(d.id);
          return <button key={d.id} type="button" onClick={() => onToggle(d.id)} aria-pressed={isOn} className={cn('relative flex flex-col items-start gap-3 rounded-2xl border-2 bg-white p-5 text-left transition-all', isOn ? 'border-salesforce-500 shadow-card-lg ring-2 ring-salesforce-500/10' : 'border-gray-200 shadow-card hover:border-gray-300')} data-id="element-154" data-mp="3munm90jv5qoh">
                <span className={cn('flex h-6 w-6 items-center justify-center rounded-full border-2 transition-colors', 'absolute right-4 top-4', isOn ? 'border-salesforce-500 bg-salesforce-500 text-white' : 'border-gray-300 bg-white')} data-id="element-155" data-mp="2u7jfor54h4al">
                  {isOn && <CheckIcon className="h-3.5 w-3.5" data-id="element-156" data-mp="287c0hkgx02ww" />}
                </span>
                <img src={d.icon} alt="" className="h-12 w-12 rounded-xl" data-id="element-157" data-mp="23gna68gz1bzc" />
                <div data-id="element-158" data-mp="23esr6zp9ftxw">
                  <p className="text-base font-semibold text-gray-900" data-id="element-159" data-mp="1zfjublyxbfy4">
                    {d.name}
                  </p>
                  <p className="mt-0.5 text-sm text-gray-500" data-id="element-160" data-mp="1uggzeucnqq3v">
                    {d.description}
                  </p>
                </div>
              </button>;
        })}
        </div>
      </section>

      {/* Vendor routing summary */}
      <AnimatePresence initial={false} data-id="element-161" data-mp="1kjugwc7t0b50">
        {(teliSelected || five9Selected) && <motion.section initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="overflow-visible" data-id="element-162" data-mp="1giipc15ddfu6">
            <div className="rounded-2xl border border-gray-200 bg-white shadow-card" data-id="element-163" data-mp="11kzecv7gsh82">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-5 py-3.5" data-id="element-164" data-mp="16bz9pw0xqr8g">
                <div className="flex items-center gap-2" data-id="element-165" data-mp="1ad1awieexdd2">
                  <RouteIcon className="h-4 w-4 text-salesforce-600" data-id="element-166" data-mp="dntol85yz083" />
                  <h3 className="text-sm font-semibold text-gray-900" data-id="element-167" data-mp="dpob2qww30ea">
                    Vendor-based routing
                  </h3>
                </div>
                {multiVendor && <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700" data-id="element-168" data-mp="mmyoaht9ozlu">
                    <SplitIcon className="h-3.5 w-3.5" data-id="element-169" data-mp="1y3xba3wxpgvx" />
                    {routing.length} vendors detected — batch will split into{' '}
                    {routing.length} routing groups
                  </span>}
              </div>

              <div className="divide-y divide-gray-100" data-id="element-170" data-mp="2yxnln23d9wl">
                {routing.map(r => <div key={r.vendor} className="px-5 py-4" data-id="element-171" data-mp="3v3rqbibx0h1b">
                    <div className="mb-3 flex flex-wrap items-center gap-2" data-id="element-172" data-mp="cz8015gaydap">
                      <span className="rounded-lg bg-gray-100 px-2.5 py-1 text-sm font-semibold text-gray-800" data-id="element-173" data-mp="7zlmeuwkav1d">
                        {r.vendor}
                      </span>
                      <span className="text-xs text-gray-500" data-id="element-174" data-mp="3gdc543ji1935">
                        {formatNumber(r.rowCount)} rows
                      </span>
                      {r.autoSelected ? <span className="inline-flex items-center gap-1 rounded-full bg-salesforce-50 px-2 py-0.5 text-[11px] font-medium text-salesforce-700" data-id="element-175" data-mp="3q6j8jiuoif0l">
                          <SparklesIcon className="h-3 w-3" data-id="element-176" data-mp="2xo5hc23ulrah" />
                          Auto-selected
                        </span> : <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700" data-id="element-177" data-mp="2snzur0cfquz9">
                          <PencilIcon className="h-3 w-3" data-id="element-178" data-mp="1cza4ildof8pw" />
                          Manually overridden
                        </span>}
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2" data-id="element-179" data-mp="137xe7y7j0qlz">
                      {teliSelected && <div data-id="element-180" data-mp="1qzckg22fbs2i">
                          <label className="mb-1 block text-xs font-medium text-gray-500" data-id="element-181" data-mp="1qz2ozyym0spf">
                            Teli AI campaign
                          </label>
                          <SearchableSelect options={teliOptions} value={r.teliCampaignId} onChange={v => setTeli(r.vendor, v)} placeholder="Select campaign…" searchPlaceholder="Search campaigns…" ariaLabel={`Teli AI campaign for ${r.vendor}`} data-id="element-182" data-mp="1hwjsrxpbxec5" />
                        </div>}
                      {five9Selected && <div data-id="element-183" data-mp="y83ute93fsg4">
                          <label className="mb-1 block text-xs font-medium text-gray-500" data-id="element-184" data-mp="t6obbqmkdtzu">
                            Five9 list
                          </label>
                          <SearchableSelect options={five9Options} value={r.five9ListId} onChange={v => setFive9(r.vendor, v)} placeholder="Select list…" searchPlaceholder="Search lists…" ariaLabel={`Five9 list for ${r.vendor}`} data-id="element-185" data-mp="o9xuh12xqudd" />
                        </div>}
                    </div>
                  </div>)}
              </div>
            </div>
          </motion.section>}
      </AnimatePresence>

      {/* Footer */}
      <div className="flex items-center justify-between" data-id="element-186" data-mp="277klvijikeaj">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50" data-id="element-187" data-mp="2c5m6i7wl8lo4">
          <ArrowLeftIcon className="h-4 w-4" data-id="element-188" data-mp="2m4jr4zqw7v1u" />
          Back to mapping
        </button>
        <button type="button" disabled={!canContinue} onClick={onContinue} className={cn('inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors', canContinue ? 'bg-salesforce-500 hover:bg-salesforce-600' : 'cursor-not-allowed bg-gray-300')} data-id="element-189" data-mp="2q4c37mfhwjby">
          Review batch
          <ArrowRightIcon className="h-4 w-4" data-id="element-190" data-mp="39r9rlwnlfhkc" />
        </button>
      </div>
    </div>;
}